import React, { useState, useEffect, useRef } from 'react';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { cancelJob } from '../helpers/apiCalls';
import useModal from '../context/ModalContext';
import generateCVS from '../helpers/generateCVS';
import '../../../css/notifications.css';

const CountdownTimer = ({ scheduledTime, onComplete, onCancel }) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(scheduledTime) - new Date();
      return Math.max(0, Math.floor(difference / 1000));
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [scheduledTime, onComplete]);

  
  return (
    <div className="mb-2 p-2 bg-yellow-50 rounded text-xs">
      <div className="font-medium text-yellow-800 mb-2">
        Starting in {timeLeft}{timeLeft > 1 ? ' seconds' : ' second'}...
      </div>
      <button 
        onClick={onCancel}
        className="w-full text-white font-medium py-1 px-2 rounded"
        style={{ backgroundColor: '#ef4444' }}
      >
        Cancel Job
      </button>
    </div>
  );
};

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState(new Set());
  const timersRef = useRef(new Map());
  const { setContent } = useModal();

  const createAutoRemoveTimer = (notification) => {
    const timeToLive = 20000;
    const elapsed = Date.now() - notification.timestamp.getTime();
    const remaining = Math.max(0, timeToLive - elapsed);
    
    const timer = setTimeout(() => {
      removeNotification(notification.id);
    }, remaining);
    
    timersRef.current.set(notification.id, timer);
  };

  const removeNotification = (id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const showErrorDetails = (errorDetails, errorCount, title = 'Bulk Job Errors') => {
    const ErrorTable = () => (
      <div className="overflow-auto max-h-96 space-y-4">
        {errorDetails.map((error, index) => (
          <div key={index} className="border-b border-gray-200 last:border-b-0">
            <div className="text-sm font-medium text-gray-900">
              {error.email}
            </div>
            <div className="text-sm text-red-600">
              {error.error}
            </div>
          </div>
        ))}
      </div>
    );

    setContent({
      modalTitle: `${title} (${errorCount} errors)`,
      content: <ErrorTable />
    });
  };

  const handleDownload = (download_url, file_name, csv_data) => {
    if (csv_data) {
      generateCVS(csv_data, file_name || 'report.csv');
    } else if (download_url) {
      const blob = new Blob([download_url], { type: 'text/csv' });
      const urlLink = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlLink;
      link.setAttribute('download', file_name || 'report.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(urlLink);
    }
  };
 
  const handleCancelJob = async (jobId) => {
    try {
      await cancelJob(jobId);
      setNotifications(prev => prev.map(notification => {
        if (notification.data?.job_id === jobId) {
          return {
            ...notification,
            data: {
              ...notification.data,
              cancelled: true
            }
          };
        }
        return notification;
      }));
    } catch (error) {
      console.error("Error cancelling job:", error);
    }
  };

  const handleCountdownComplete = (jobId) => {
    setNotifications(prev => prev.map(notification => {
      if (notification.data?.job_id === jobId || notification.data?.jobId === jobId) {
        return {
          ...notification,
          message: "Initializing process...",
          data: {
            ...notification.data,
            countdownCompleted: true
          }
        };
      }
      return notification;
    }));
  };

  const findExistingNotification = (notifications, jobId, level) => {
    return notifications.findIndex(notification => 
      (notification.data?.job_id === jobId || notification.data?.jobId === jobId) &&
      notification.level === level
    );
  };

  useNotificationSocket({
    onMessage: (payload) => {
      setNotifications((prev) => {
        const newNotification = {
          ...payload,
          id: Date.now() + Math.random(),
          timestamp: new Date(payload.timestamp || Date.now())
        };
        
        const jobId = newNotification.data?.job_id || newNotification.data?.jobId;
        
        if (jobId) {
          const existingIndex = findExistingNotification(prev, jobId, newNotification.level);
          
          if (existingIndex !== -1) {
            const updatedNotifications = [...prev];
            const existingNotification = updatedNotifications[existingIndex];
            
            const oldTimer = timersRef.current.get(existingNotification.id);
            if (oldTimer) {
              clearTimeout(oldTimer);
              timersRef.current.delete(existingNotification.id);
            }
            
            updatedNotifications[existingIndex] = {
              ...existingNotification,
              ...newNotification,
              id: existingNotification.id,
              timestamp: existingNotification.timestamp
            };
            
            // DISABLED FOR NOW
            // Create new timer for the updated notification
            // createAutoRemoveTimer(updatedNotifications[existingIndex]);
            
            return updatedNotifications;
          }
        }
        
        setNewNotifications(prev => new Set([...prev, newNotification.id]));
        
        setTimeout(() => {
          setNewNotifications(prev => {
            const newSet = new Set(prev);
            newSet.delete(newNotification.id);
            return newSet;
          });
        }, 1500); // Match animation duration (1.5s)
        
        // DISABLED FOR NOW
        // Create auto-remove timer for this new notification
        // createAutoRemoveTimer(newNotification);
        
        const result = [newNotification, ...prev].slice(0, 5);
        return result;
      });
    }
  });

  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    notifications.forEach(notification => {
      if (notification.data && 
          notification.data.processed !== undefined && 
          notification.data.total !== undefined &&
          notification.data.processed === notification.data.total) {
        
        if (!timersRef.current.has(notification.id)) {
          const timer = setTimeout(() => {
            removeNotification(notification.id);
            timersRef.current.delete(notification.id);
          }, 10000);
          
          timersRef.current.set(notification.id, timer);
        }
      }
    });
  }, [notifications]);

  const getColorClass = (level) => {
    switch (level) {
      case 'success':
        return 'border-green-500 bg-green-50';
      case 'error':
        return 'border-red-500 bg-red-50';
      case 'warning':
        return 'border-yellow-500 bg-yellow-50';
      default:
        return 'border-blue-500 bg-blue-50';
    }
  };

  const getColorStyle = (level) => {
    switch (level) {
      case 'success':
        return { borderLeftColor: '#10b981', backgroundColor: '#f0fdf4' };
      case 'error':
        return { borderLeftColor: '#ef4444', backgroundColor: '#fef2f2' };
      case 'warning':
        return { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' };
      default:
        return { borderLeftColor: '#3b82f6', backgroundColor: '#eff6ff' };
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      <div className="fixed top-24 right-4 space-y-2 flex flex-col items-end" style={{ zIndex: 1000 }}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification-item w-80 p-4 rounded-lg shadow-lg border bg-white ${getColorClass(notification.level)} ${
              newNotifications.has(notification.id) ? 'fade-in' : ''
            }`}
            style={{
              ...getColorStyle(notification.level),
              borderLeftWidth: '4px',
              borderLeftStyle: 'solid'
            }}
          >
            <div className="flex items-start space-x-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 mb-1">
                  {notification.message.split('\n').map((line, index) => {
                    if (line.includes('Errors:') && notification.data?.error_details?.length > 0) {
                      const parts = line.split('Errors:');
                      const title = (() => {
                        const msg = (notification.message || '').toLowerCase();
                        if (msg.includes('participant removal')) return 'Bulk Cohort Removal Errors';
                        if (msg.includes('participant addition') || msg.includes('adding participants')) return 'Bulk Cohort Addition Errors';
                        if (msg.includes('invite') || msg.includes('invitation')) return 'Bulk Invitation Errors';
                        if (msg.includes('user')) return 'Bulk User Import Errors';
                        if (msg.includes('activity')) return 'Bulk Activity Report Errors';
                        return 'Bulk Job Errors';
                      })();
                      return (
                        <div key={index}>
                          {parts[0]}Errors: {parts[1]}
                        <button
                          onClick={() => showErrorDetails(notification.data.error_details, notification.data.error_count, title)}
                          className="ml-1 text-blue-600 hover:text-blue-800 underline text-xs"
                        >
                          (see details)
                        </button>
                        </div>
                      );
                    }
                    return <div key={index}>{line}</div>;
                  })}
                </div>
                
                {notification.data?.scheduled_start && !notification.data?.cancelled && !notification.data?.processed && !notification.data?.countdownCompleted ? (
                   <CountdownTimer 
                     scheduledTime={notification.data.scheduled_start}
                     onComplete={() => handleCountdownComplete(notification.data.job_id || notification.data.jobId)}
                     onCancel={() => handleCancelJob(notification.data.job_id || notification.data.jobId)}
                   />
                ) : notification.data?.cancelled && notification.data?.scheduled_start ? (
                  <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
                    <div className="mt-2 font-medium" style={{ color: '#ef4444' }}>Job Cancelled!</div>
                  </div>
                ) : (
                  null
                )}
                
                {notification.data && notification.data.job_id && notification.data.processed !== undefined && notification.data.processed !== notification.data.total && (
                  <div className="mb-2 p-2 bg-gray-50 rounded text-xs">
                    <div><strong>Progress:</strong> {notification.data.processed}/{notification.data.total} ({notification.data.percent}%)</div>
                    {!notification.data.cancelled ? (
                      <button 
                        onClick={() => handleCancelJob(notification.data.job_id)}
                        className="w-full text-white font-medium py-1 px-2 rounded"
                        style={{ backgroundColor: '#ef4444' }}
                      >
                        Cancel Job
                      </button>
                    ) : (
                      <div className="mt-2 font-medium" style={{ color: '#ef4444' }}>Job Cancelled!</div>
                    )}
                  </div>
                )}
                
                {((notification.data?.download_url && notification.data?.file_name) || (notification.data?.csv_data && notification.data?.file_name)) && (
                  <button 
                    onClick={() => handleDownload(notification.data?.download_url, notification.data?.file_name, notification.data?.csv_data)}
                    className="download-button"
                    style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    Download {notification.data?.file_name || 'report.csv'}
                  </button>
                )}
              </div>
              
              <button
                onClick={() => removeNotification(notification.id)}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default NotificationCenter;
